import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaCheck, FaClock, FaRegCircle, FaTimes } from "react-icons/fa";
import {
  TimelineItem,
  SplitTimelineMap,
  SplitStatusEnum,
  SplitStatusLabels,
} from "@/models/splitStatusEnum.model";
import { format } from "date-fns";

interface SplitTimelineProps {
  timeline: SplitTimelineMap;
  /** "current" mostra só a solicitação em andamento; "history", todas. */
  mode?: "current" | "history";
}

const formatTimestamp = (timestamp: string) => {
  try {
    return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm");
  } catch {
    return timestamp;
  }
};

const getStatusIcon = (item: TimelineItem) => {
  if (item.rejected) return FaTimes;
  if (item.completed) return FaCheck;
  if (item.inProgress) return FaClock;
  return FaRegCircle;
};

/** Cor base (sem sufixo) do marcador do item. */
const getStatusScheme = (item: TimelineItem) => {
  if (item.rejected) return "red";
  if (item.completed) return "green";
  if (item.inProgress) return "yellow";
  return "gray";
};

const isTimelineItem = (value: unknown): value is TimelineItem =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  "status" in (value as Record<string, unknown>);

/** Aceita array de eventos, evento solto ou mapa de eventos. */
const toEvents = (value: unknown): TimelineItem[] => {
  if (Array.isArray(value)) return value.filter(isTimelineItem);
  if (isTimelineItem(value)) return [value];
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).filter(isTimelineItem);
  }
  return [];
};

/**
 * Reconstrói os limites entre solicitações a partir dos eventos: todo CREATED
 * inicia um novo processo. Usado quando o agrupamento original se perdeu.
 */
const splitByProcess = (events: TimelineItem[]): TimelineItem[][] => {
  const groups: TimelineItem[][] = [];

  events.forEach((event) => {
    if (groups.length === 0 || event.status === SplitStatusEnum.CREATED) {
      groups.push([event]);
      return;
    }
    groups[groups.length - 1].push(event);
  });

  return groups;
};

/**
 * Normaliza a timeline em grupos por solicitação. Registros antigos podem vir
 * como lista plana de eventos — ou mista, já que updateLastTimeline no backend
 * espalha array dentro de objeto — e aí o agrupamento é refeito pelos eventos.
 */
const normalizeTimeline = (
  timeline: SplitTimelineMap
): { key: number; events: TimelineItem[] }[] => {
  if (!timeline || typeof timeline !== "object") return [];

  const keys = Object.keys(timeline)
    .map(Number)
    .filter((key) => !Number.isNaN(key))
    .sort((a, b) => a - b);

  const values = keys.map(
    (key) => (timeline as Record<string, unknown>)[String(key)]
  );

  // Qualquer valor que seja um evento solto denuncia formato plano/misto.
  if (values.some(isTimelineItem)) {
    return splitByProcess(values.flatMap(toEvents)).map((events, index) => ({
      key: index,
      events,
    }));
  }

  return keys
    .map((key, index) => ({ key, events: toEvents(values[index]) }))
    .filter((group) => group.events.length > 0);
};

/** Lista vertical de eventos de uma única solicitação. */
function TimelineEvents({ items }: { items: TimelineItem[] }) {
  return (
    <VStack spacing={0} align="stretch" w="100%">
      {items.map((item, index) => {
        const IconComponent = getStatusIcon(item);
        const scheme = getStatusScheme(item);
        const isPending = !item.completed && !item.rejected && !item.inProgress;
        const isLast = index === items.length - 1;

        return (
          <HStack key={index} spacing={4} align="stretch">
            {/* Marcador + conector */}
            <VStack spacing={0} align="center" flexShrink={0}>
              <Flex
                align="center"
                justify="center"
                boxSize={8}
                borderRadius="full"
                bg={isPending ? "white" : `${scheme}.500`}
                color={isPending ? "gray.400" : "white"}
                borderWidth={isPending ? "2px" : "0"}
                borderColor="gray.200"
                boxShadow={isPending ? "none" : "sm"}
              >
                <Icon as={IconComponent} boxSize={3.5} />
              </Flex>
              {!isLast && <Box flex="1" w="2px" bg="gray.200" minH={4} />}
            </VStack>

            {/* Conteúdo */}
            <Box flex="1" pb={isLast ? 0 : 5}>
              <Box
                borderRadius="lg"
                borderWidth="1px"
                borderColor={item.rejected ? "red.100" : "gray.200"}
                bg={item.rejected ? "red.50" : isPending ? "gray.50" : "white"}
                px={4}
                py={3}
              >
                <Flex
                  justify="space-between"
                  align={{ base: "start", md: "center" }}
                  gap={2}
                  direction={{ base: "column", md: "row" }}
                >
                  <Text
                    fontWeight="semibold"
                    fontSize="sm"
                    color={isPending ? "gray.500" : "gray.800"}
                  >
                    {SplitStatusLabels[item.status]}
                  </Text>
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                    {formatTimestamp(item.timestamp)}
                  </Text>
                </Flex>

                {item.changedBy && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    por {item.changedBy}
                  </Text>
                )}

                {item.reason && (
                  <Text
                    fontSize="xs"
                    color={item.rejected ? "red.600" : "gray.600"}
                    mt={2}
                    fontStyle="italic"
                  >
                    {item.reason}
                  </Text>
                )}
              </Box>
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
}

export default function SplitTimeline({
  timeline,
  mode = "current",
}: SplitTimelineProps) {
  const groups = normalizeTimeline(timeline);

  if (groups.length === 0) return null;

  // O último grupo é sempre o processo corrente: o backend abre uma chave nova
  // a cada nova solicitação e só escreve na última.
  const currentKey = groups[groups.length - 1].key;
  const visible = mode === "history" ? groups : [groups[groups.length - 1]];

  return (
    <VStack spacing={6} align="stretch" w="100%">
      {visible.map((group) => {
        const isCurrent = group.key === currentKey;

        return (
          <Box key={group.key}>
            <HStack mb={3} spacing={2}>
              <Badge
                colorScheme={isCurrent ? "green" : "gray"}
                borderRadius="full"
                px={2.5}
                py={1}
                fontSize="10px"
                textTransform="none"
              >
                {isCurrent ? "Processo atual" : "Encerrada"}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                Solicitação {group.key + 1}
              </Text>
            </HStack>
            <TimelineEvents items={group.events} />
          </Box>
        );
      })}
    </VStack>
  );
}
